// src/pages/HubSpotCallback.jsx
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { CheckCircle, XCircle } from 'lucide-react';
import styles from './HubSpotCallback.module.css';

export default function HubSpotCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const oauthError = searchParams.get('error');

        if (oauthError) throw new Error(`HubSpot OAuth error: ${oauthError}`);
        if (!code) throw new Error('No authorization code received');

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const session = (await supabase.auth.getSession()).data.session;
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/hubspot/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ code, state }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to connect HubSpot');
        }

        setStatus('success');
        setTimeout(() => navigate('/dashboard', { state: { message: 'HubSpot connected successfully!' } }), 2000);
      } catch (err) {
        console.error('HubSpot OAuth callback error:', err);
        setError(err.message);
        setStatus('error');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {status === 'processing' && (
          <>
            <div className={`${styles.iconBadge} ${styles['iconBadge--processing']}`}>
              <div className={styles.spinner} />
            </div>
            <h2 className={styles.title}>Connecting HubSpot…</h2>
            <p className={styles.message}>Please wait while we complete the connection.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className={`${styles.iconBadge} ${styles['iconBadge--success']}`}>
              <CheckCircle size={32} aria-hidden="true" />
            </div>
            <h2 className={styles.title}>HubSpot Connected!</h2>
            <p className={styles.message}>Your HubSpot CRM is now connected. Redirecting to dashboard…</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className={`${styles.iconBadge} ${styles['iconBadge--error']}`}>
              <XCircle size={32} aria-hidden="true" />
            </div>
            <h2 className={styles.title}>Connection Failed</h2>
            <p className={styles.message}>{error || 'Failed to connect HubSpot. Please try again.'}</p>
            <button onClick={() => navigate('/dashboard')} className={`btn-primary ${styles.btnBack}`}>
              Back to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
